/**
 * InfraHook · home-page premium AI recon widget
 * ---------------------------------------------------------------------------
 * Redesigned to feel like a proper AI tool suite — not a chip row.
 *
 * Flow:
 *   1. Landing state → big colored tool tiles (5 total). One tap.
 *   2. Tap a tile → the tile expands into an input + SCAN button, ambient
 *      accent floods the section.
 *   3. Result → rich card with quick-answer, "OPEN FULL HUB" CTA, and 2 upsell
 *      chips (tools/recovery/books depending on tool).
 *
 * Mobile-first: 2-col grid → 5-col on desktop. Every tile is at least 96px tall
 * so it's a proper tap target. Kickers/labels use Inter + monospace mix.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Radar, Globe, Fingerprint, Bug, Search, Zap, Loader2, ArrowRight,
  Terminal, Wrench, Shield, BookOpen, Sparkles, Server, ChevronLeft,
  Clock, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// ---------- TOOL CATALOGUE ----------
const TOOLS = [
  {
    id: 'ip-intel',
    label: 'IP INTEL',
    tagline: 'ASN · geo · risk',
    icon: Radar,
    color: '#4de0ff',
    apiFn: 'infraIpIntel',
    field: 'target',
    placeholder: '8.8.8.8 or example.com',
    description: 'Grabs geo, ASN, ISP and cross-references AbuseIPDB. Perfect for spotting fake traffic sources.',
  },
  {
    id: 'whois',
    label: 'WHOIS',
    tagline: 'domain registration data',
    icon: Globe,
    color: '#ffd34d',
    apiFn: 'infraWhois',
    field: 'domain',
    placeholder: 'github.com',
    description: 'Registrar, dates, name-servers, and expiry. Handy for phishing takedowns + brand ops.',
  },
  {
    id: 'subdomain',
    label: 'SUBDOMAINS',
    tagline: 'attack surface map',
    icon: Fingerprint,
    color: '#ff2d92',
    apiFn: 'infraSubdomains',
    field: 'domain',
    placeholder: 'example.com',
    description: 'Fetches every subdomain from CT logs. See what your team accidentally exposed.',
  },
  {
    id: 'cve',
    label: 'CVE LOOKUP',
    tagline: 'known vulnerabilities',
    icon: Bug,
    color: '#ff8a4d',
    apiFn: 'infraCveSearch',
    field: 'query',
    placeholder: 'CVE-2021-44228 or "log4j"',
    description: 'Live NVD search. Tells you severity + affected products + when a fix landed.',
  },
  {
    id: 'osint',
    label: 'USERNAME',
    tagline: '40+ platforms',
    icon: Search,
    color: '#00ff9d',
    apiFn: 'infraUsernameOsint',
    field: 'username',
    placeholder: 'torvalds — no @',
    description: 'Cross-platform username sweep. Insta, Twitter, GitHub, Reddit, Bluesky and more.',
  },
];

// ---------- CROSS-LINKS (always visible at the bottom) ----------
const NEXT_LINKS = [
  { to: '/hacking-servers', label: 'FULL INFRA HUB',    kicker: 'proxies · VPS · deep recon',         icon: Server,   c: '#4de0ff' },
  { to: '/tools',           label: 'AI SECURITY SUITE', kicker: '9 AI tools · scam · privacy',        icon: Terminal, c: '#c084fc' },
  { to: '/advisory',        label: 'CONSULTATION DESK', kicker: 'coached response · IG · FB · Gmail', icon: Shield,   c: '#00ff9d' },
  { to: '/books',           label: 'EDUCATION LIBRARY', kicker: 'ethical playbooks + OSINT guides',   icon: BookOpen, c: '#ffd34d' },
];

const InfraHook = () => {
  const [selected, setSelected] = useState(null);   // active tool object
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const openTool = (t) => {
    setSelected(t);
    setVal('');
    setResult(null);
    setErr('');
  };
  const closeTool = () => { setSelected(null); setResult(null); setErr(''); setVal(''); };

  const run = async (e) => {
    e?.preventDefault?.();
    if (!val.trim()) { toast.error('Enter a value first'); return; }
    setErr(''); setBusy(true); setResult(null);
    try {
      const out = await api[selected.apiFn]({ [selected.field]: val.trim() });
      setResult(out);
    } catch (ex) {
      setErr(ex?.detail?.message || ex.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="relative py-14 sm:py-20 overflow-hidden"
      data-testid="home-infra-hook"
      style={{
        // Ambient tinted background responds to the active tool
        background: selected
          ? `radial-gradient(55% 65% at 50% 10%, ${selected.color}0d 0%, transparent 65%)`
          : 'transparent',
        transition: 'background .5s ease',
      }}
    >
      {/* Ambient blobs — subtle */}
      <div aria-hidden className="pointer-events-none absolute -top-16 -left-20 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#4de0ff' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-16 -right-20 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#c084fc' }} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header — always visible */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] eh-mono tracking-widest mb-5" style={{ borderColor: 'rgba(255,211,77,.35)', background: 'rgba(255,211,77,.05)', color: '#ffd34d' }}>
            <Sparkles size={11} /> // PUBLIC OPS · TRY LIVE
          </div>
          <h2
            className="font-black leading-[1.05]"
            style={{ fontSize: 'clamp(2rem, 5.5vw, 3.6rem)', fontFamily: 'Cinzel, serif' }}
          >
            <span className="block opacity-90">FREE AI RECON</span>
            <span
              className="block"
              style={{
                background: 'linear-gradient(90deg, #4de0ff, #00ff9d, #ffd34d)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(0,255,157,.35))',
              }}
            >
              IN ONE TAP.
            </span>
          </h2>
          <p className="opacity-75 mt-4 max-w-xl mx-auto text-[13.5px] sm:text-sm leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            Instant intel on any IP, domain, CVE or username. Spot risk, then let our operators do the deep work.
          </p>

          {/* Free-scans chip — a subtle "premium tier" nudge */}
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] eh-mono tracking-widest" style={{ borderColor: 'rgba(0,255,157,.35)', background: 'rgba(0,255,157,.05)', color: '#00ff9d' }}>
            <Clock size={10} /> 5 FREE SCANS PER TOOL · PER HOUR
          </div>
        </div>

        {/* ---- STATE 1: TOOL GRID ---- */}
        {!selected && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {TOOLS.map(t => {
                const I = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => openTool(t)}
                    className="tool-tile relative rounded-2xl border-2 p-5 sm:p-6 text-left overflow-hidden group"
                    style={{
                      borderColor: `${t.color}55`,
                      background: `linear-gradient(160deg, ${t.color}0c 0%, transparent 65%)`,
                      '--c': t.color,
                    }}
                    data-testid={`infra-tool-${t.id}`}
                  >
                    <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${t.color}, transparent)` }} />

                    <div className="flex items-start justify-between mb-3">
                      <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${t.color}1a`, border: `1px solid ${t.color}55`, color: t.color }}>
                        <I size={18} strokeWidth={1.8} />
                      </div>
                      <span className="eh-mono text-[8.5px] tracking-widest font-black px-1.5 py-0.5 rounded-full" style={{ background: `${t.color}18`, color: t.color, border: `1px solid ${t.color}55` }}>
                        FREE
                      </span>
                    </div>

                    <div className="eh-mono text-[9.5px] tracking-widest opacity-70 mb-1" style={{ color: t.color }}>// {t.tagline}</div>
                    <div className="font-black text-[15px] sm:text-base mb-1.5" style={{ fontFamily: 'Inter,sans-serif' }}>{t.label}</div>
                    <p className="text-[11.5px] opacity-70 leading-relaxed line-clamp-2 sm:line-clamp-3" style={{ fontFamily: 'Inter,sans-serif' }}>{t.description}</p>

                    <div className="mt-4 inline-flex items-center gap-1 eh-mono text-[10px] tracking-widest font-black transition-transform group-hover:translate-x-0.5" style={{ color: t.color }}>
                      RUN SCAN <ArrowRight size={11} />
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* ---- STATE 2: TOOL FORM ---- */}
        {selected && (
          <div
            className="max-w-2xl mx-auto rounded-2xl border-2 p-5 sm:p-7 relative overflow-hidden"
            style={{
              borderColor: `${selected.color}55`,
              background: `linear-gradient(160deg, ${selected.color}12 0%, rgba(0,0,0,0.6) 65%)`,
              boxShadow: `0 30px 80px -30px ${selected.color}80`,
            }}
            data-testid="infra-tool-open"
          >
            <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${selected.color}, transparent)` }} />

            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={closeTool}
                className="w-8 h-8 rounded-lg grid place-items-center border transition-colors"
                style={{ borderColor: 'var(--eh-border)', color: 'rgba(255,255,255,.75)' }}
                data-testid="infra-tool-close"
                aria-label="Back to tools"
              >
                <ChevronLeft size={14} />
              </button>
              <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${selected.color}1a`, border: `1px solid ${selected.color}55`, color: selected.color }}>
                <selected.icon size={18} strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="eh-mono text-[10px] tracking-widest opacity-70" style={{ color: selected.color }}>// {selected.tagline}</div>
                <div className="font-black text-lg leading-tight" style={{ fontFamily: 'Inter,sans-serif' }}>{selected.label}</div>
              </div>
            </div>

            <p className="text-[13px] opacity-80 leading-relaxed mb-4" style={{ fontFamily: 'Inter,sans-serif' }}>{selected.description}</p>

            <form onSubmit={run} className="flex flex-col sm:flex-row gap-2" data-testid="infra-tool-form">
              <input
                autoFocus
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={selected.placeholder}
                className="flex-1 px-4 py-3 rounded-lg border text-sm bg-black/40 outline-none focus:ring-2"
                style={{
                  borderColor: `${selected.color}45`,
                  color: '#fff',
                  fontFamily: 'ui-monospace, "Share Tech Mono", monospace',
                }}
                data-testid="infra-tool-input"
              />
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-black text-[12px] tracking-widest transition-all disabled:opacity-50 hover:brightness-110 hover:-translate-y-0.5"
                style={{ background: selected.color, color: '#04120a', boxShadow: `0 12px 28px -10px ${selected.color}90` }}
                data-testid="infra-tool-run"
              >
                {busy ? <><Loader2 size={13} className="animate-spin" /> SCANNING</> : <><Zap size={13} strokeWidth={2.5} /> RUN SCAN</>}
              </button>
            </form>

            {err && (
              <div className="mt-4 p-3 rounded-lg border text-xs" style={{ borderColor: 'rgba(255,80,120,.4)', background: 'rgba(255,80,120,.08)', color: '#ffb7c5', fontFamily: 'Inter,sans-serif' }} data-testid="infra-tool-error">
                {err}
              </div>
            )}

            {result && <QuickResult mode={selected.id} data={result} accent={selected.color} />}
          </div>
        )}

        {/* Cross-service link strip — always visible */}
        <div className="mt-10 sm:mt-14">
          <div className="text-center mb-4">
            <div className="eh-mono text-[10px] tracking-widest opacity-60">// AFTER YOUR SCAN — WHERE TO GO NEXT</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="infra-hook-crosslinks">
            {NEXT_LINKS.map(l => {
              const Icon = l.icon;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className="hook-crosslink group"
                  style={{ '--c': l.c }}
                  data-testid={`infra-hook-link-${l.to.replace(/\//g, '')}`}
                >
                  <div className="hook-crosslink-icon"><Icon size={15} strokeWidth={1.9} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11.5px] font-bold truncate" style={{ color: l.c, fontFamily: 'Inter,sans-serif' }}>{l.label}</div>
                    <div className="text-[9.5px] opacity-70 truncate">{l.kicker}</div>
                  </div>
                  <ArrowRight size={11} className="shrink-0 opacity-50 group-hover:translate-x-0.5 group-hover:opacity-100 transition-all" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .tool-tile { transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .35s, background .3s; cursor: pointer; }
        .tool-tile:hover { transform: translateY(-4px); border-color: var(--c) !important; box-shadow: 0 24px 60px -25px color-mix(in srgb, var(--c) 55%, transparent); }
        .tool-tile:active { transform: translateY(-1px); }
        .hook-crosslink {
          display: flex; align-items: center; gap: 10px;
          padding: 11px 13px; border-radius: 12px;
          background: linear-gradient(160deg, color-mix(in srgb, var(--c) 10%, transparent), rgba(0,0,0,.42));
          border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
          transition: transform .22s ease, border-color .2s ease, box-shadow .3s ease;
        }
        .hook-crosslink:hover { transform: translateY(-2px); border-color: var(--c); box-shadow: 0 14px 32px -14px color-mix(in srgb, var(--c) 55%, transparent); }
        .hook-crosslink-icon {
          width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
          background: color-mix(in srgb, var(--c) 14%, transparent);
          border: 1px solid color-mix(in srgb, var(--c) 45%, transparent);
          color: var(--c);
          flex-shrink: 0;
        }
      `}</style>
    </section>
  );
};

// -----------------------------------------------------------------------
// Compact result renderer — surfaces the money-line + a paid upsell chip
// -----------------------------------------------------------------------
const QuickResult = ({ mode, data, accent }) => {
  const summary = (() => {
    if (mode === 'ip-intel')  return `${data.geo?.country || '—'} · ${data.geo?.isp || 'unknown ISP'} · risk ${data.risk?.confidence_pct || 0}%`;
    if (mode === 'whois')     return `Registrar: ${data.parsed?.registrar || 'unknown'} · Expires: ${(data.parsed?.expiration || '—').slice(0, 10)}`;
    if (mode === 'subdomain') return `${data.count} subdomains found · source: ${data.source}`;
    if (mode === 'cve')       return `${data.count} vulnerabilities · top severity: ${data.results?.[0]?.severity || '—'}`;
    if (mode === 'osint')     return `Found on ${data.found_count}/${data.checked} platforms`;
    return '';
  })();
  return (
    <div
      className="mt-5 p-4 rounded-lg border-2"
      style={{ borderColor: `${accent}55`, background: `${accent}0a` }}
      data-testid="infra-hook-result"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="eh-mono text-[10px] tracking-widest" style={{ color: accent }}>// SCAN RESULT</div>
        <Link
          to="/hacking-servers"
          className="eh-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full border-2 font-black hover:brightness-110"
          style={{ color: accent, borderColor: accent, background: `${accent}18` }}
        >
          FULL SCAN <ArrowRight size={10} className="inline" />
        </Link>
      </div>

      <div className="text-[14px] font-bold leading-snug" style={{ color: accent, fontFamily: 'Inter,sans-serif' }}>{summary}</div>

      {data.next_steps?.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: `${accent}22` }}>
          <div className="eh-mono text-[10px] tracking-widest mb-2" style={{ color: '#ffd34d' }}>// NEXT STEP · LET US DO IT</div>
          <div className="space-y-2">
            {data.next_steps.slice(0, 2).map((it, i) => (
              <Link
                key={i}
                to={it.to || '/hacking-servers'}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border transition-transform hover:-translate-y-0.5"
                style={{ borderColor: `${it.accent}55`, background: `${it.accent}0d` }}
                data-testid={`infra-hook-upsell-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-[12.5px] font-bold" style={{ fontFamily: 'Inter,sans-serif' }}>{it.title}</div>
                  <div className="text-[10.5px] opacity-70">{it.kicker}</div>
                </div>
                <span
                  className="shrink-0 px-2.5 py-1 rounded-full text-[9.5px] eh-mono tracking-widest font-black inline-flex items-center gap-1"
                  style={{ color: '#04120a', background: it.accent }}
                >
                  {it.cta} <ExternalLink size={9} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfraHook;
